import {
  Mail,
  MessageCircle,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Send,
  ChevronDown,
  Loader2,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import StatusBadge from "@/components/StatusBadge";
import ScoreIndicator from "@/components/ScoreIndicator";
import { CandidateOriginBadge } from "@/lib/candidateOrigin";

/**
 * Detailed candidate card showing the full AI analysis inline.
 *
 * Three render modes:
 *  - `status === "pendente"` → compact "Analisando…" row
 *  - `status === "erro"`     → compact failure row with **Reanalisar** + Excluir
 *  - default                  → full analysis card
 */
const CandidateCard = ({
  candidate: c,
  rank,
  onOpen,
  onReanalyze,
  onDelete,
  // Selection mode (for "Compare candidates" feature)
  selectable = false,
  selected = false,
  onToggleSelect,
  selectionDisabled = false,
}) => {
  const isPending = c.status === "pendente";
  const isError = c.status === "erro";
  const [busy, setBusy] = useState(false);

  const handleToggle = (e) => {
    e?.stopPropagation();
    if (selectionDisabled && !selected) return;
    onToggleSelect?.(c);
  };

  const SelectCheckbox = selectable ? (
    <button
      type="button"
      onClick={handleToggle}
      disabled={selectionDisabled && !selected}
      data-testid={`select-candidate-${c.id}`}
      aria-pressed={selected}
      aria-label={selected ? "Desmarcar candidato" : "Selecionar para comparar"}
      title={
        selectionDisabled && !selected
          ? "Limite de candidatos selecionáveis atingido"
          : selected
          ? "Desmarcar"
          : "Selecionar para comparar"
      }
      className={`shrink-0 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${
        selected
          ? "bg-brand-blue border-brand-blue text-white"
          : "bg-white border-slate-300 hover:border-brand-blue text-transparent disabled:opacity-40 disabled:cursor-not-allowed"
      }`}
    >
      <CheckCircle2 className="w-3.5 h-3.5" />
    </button>
  ) : null;

  const handleReanalyze = async (e) => {
    e?.stopPropagation();
    if (busy || !onReanalyze) return;
    setBusy(true);
    try {
      await onReanalyze(c);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (e) => {
    e?.stopPropagation();
    if (busy || !onDelete) return;
    if (!window.confirm("Excluir este candidato?")) return;
    setBusy(true);
    try {
      await onDelete(c);
    } finally {
      setBusy(false);
    }
  };

  if (isPending) {
    return (
      <div
        data-testid={`candidate-card-${c.id}`}
        className={`bg-white border ${selected ? "border-brand-blue ring-2 ring-brand-blue/30" : "border-blue-200"} rounded-lg p-5 flex items-center gap-4`}
      >
        {SelectCheckbox}
        {rank ? (
          <div className="shrink-0 w-9 h-9 rounded-md bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-semibold tabular-nums">
            #{rank}
          </div>
        ) : null}
        <Loader2 className="w-5 h-5 text-blue-600 animate-spin shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">
            {c.filename || c.name}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Analisando com IA — extraindo texto, identificando contatos e calculando score...
          </div>
        </div>
        <span className="text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-md font-medium">
          Pendente
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        data-testid={`candidate-card-${c.id}`}
        className={`bg-red-50/30 border ${selected ? "border-brand-blue ring-2 ring-brand-blue/30" : "border-red-200"} rounded-lg p-5`}
      >
        <div className="flex items-start gap-4">
          {SelectCheckbox}
          {rank ? (
            <div className="shrink-0 w-9 h-9 rounded-md bg-red-100 text-red-700 flex items-center justify-center text-sm font-semibold tabular-nums">
              #{rank}
            </div>
          ) : null}
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-slate-900 truncate">
                {c.name || c.filename}
              </span>
              <StatusBadge status="erro" />
            </div>
            <p className="text-xs text-red-800 leading-snug">
              {c.summary ||
                "A análise não pôde ser concluída. Tente reanalisar — o sistema vai tentar de novo o OCR e a IA. Se persistir, faça novo upload do PDF."}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleReanalyze}
              disabled={busy}
              data-testid={`reanalyze-candidate-${c.id}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-brand-blue text-white hover:bg-brand-blue-dark disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
              Reanalisar
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                data-testid={`delete-candidate-${c.id}`}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Excluir
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`candidate-card-${c.id}`}
      className={`bg-white border ${selected ? "border-brand-blue ring-2 ring-brand-blue/30" : "border-slate-200 hover:border-slate-300"} rounded-lg overflow-hidden transition-colors`}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-slate-100 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0 flex-1">
          {SelectCheckbox}
          {rank ? (
            <div className="shrink-0 w-9 h-9 rounded-md bg-brand-blue text-white flex items-center justify-center text-sm font-semibold tabular-nums">
              #{rank}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="flex items-center flex-wrap gap-2 mb-1.5">
              <h3 className="heading text-lg font-semibold text-slate-900 leading-tight">
                {c.name || c.filename}
              </h3>
              <StatusBadge status={c.status} />
              {c.email_sent && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">
                  <CheckCircle2 className="w-3 h-3" /> e-mail enviado
                </span>
              )}
              {c.whatsapp_sent && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">
                  <CheckCircle2 className="w-3 h-3" /> WhatsApp enviado
                </span>
              )}
              <CandidateOriginBadge candidate={c} />
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
              {c.email ? (
                <a
                  href={`mailto:${c.email}`}
                  data-testid={`card-email-${c.id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1.5 hover:text-slate-900"
                >
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  {c.email}
                </a>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-slate-400 italic text-xs">
                  <Mail className="w-3.5 h-3.5" />
                  sem e-mail
                </span>
              )}
              {c.phone ? (
                <span className="inline-flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5 text-slate-400" />
                  {c.phone}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-slate-400 italic text-xs">
                  <MessageCircle className="w-3.5 h-3.5" />
                  sem telefone
                </span>
              )}
              {c.experience_years > 0 && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {c.experience_years} anos de experiência
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="lg:w-44 shrink-0">
          <ScoreIndicator
            score={c.ranking_score ?? c.score}
            aiScore={c.score}
            tooltip={
              c.ranking_breakdown
                ? `IA: ${c.ranking_breakdown.ai_score}  ` +
                  `· experiência: ${c.ranking_breakdown.experience_bonus}  ` +
                  `· skills batendo (${c.matching_skills ?? 0}): ${c.ranking_breakdown.skill_bonus}  ` +
                  `· status: ${c.ranking_breakdown.status_bonus}  ` +
                  `· recência: ${c.ranking_breakdown.recency_bonus}  ` +
                  `(critérios: ${c.ranking_breakdown.achieved_bonus ?? "—"}/${c.ranking_breakdown.bonus_cap ?? 5})`
                : undefined
            }
          />
        </div>
      </div>

      {/* AI analysis body */}
      <div className="px-6 py-5 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left col: summary + justification */}
        <div className="lg:col-span-2 space-y-4">
          <section>
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">
              Resumo da IA
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {c.summary || (
                <span className="text-slate-400 italic">
                  Sem resumo disponível.
                </span>
              )}
            </p>
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5">
              Justificativa do score (IA: {c.score}%)
            </div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {c.justification || (
                <span className="text-slate-400 italic">
                  Sem justificativa disponível.
                </span>
              )}
            </p>
          </section>

          {c.skills && c.skills.length > 0 && (
            <section>
              <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-500 mb-1.5 flex items-center gap-2">
                <span>Habilidades identificadas</span>
                {(c.matching_skills ?? 0) > 0 && (
                  <span
                    data-testid={`matching-skills-${c.id}`}
                    title="Habilidades que coincidem com as exigidas pela vaga"
                    className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold normal-case tracking-normal"
                  >
                    ✓ {c.matching_skills} batem com a vaga
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {c.skills.map((s, i) => (
                  <span
                    key={`${s}-${i}`}
                    className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-md border border-slate-200"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right col: strengths + gaps */}
        <div className="space-y-4">
          <section>
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Pontos fortes
            </div>
            {c.strengths && c.strengths.length > 0 ? (
              <ul className="space-y-1.5">
                {c.strengths.map((s, i) => (
                  <li
                    key={`${s.slice(0, 40)}-${i}`}
                    className="text-sm text-slate-700 leading-snug pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-emerald-500"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-400 italic">
                Nenhum ponto forte identificado.
              </div>
            )}
          </section>

          <section>
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Pontos fracos
            </div>
            {c.gaps && c.gaps.length > 0 ? (
              <ul className="space-y-1.5">
                {c.gaps.map((g, i) => (
                  <li
                    key={`${g.slice(0, 40)}-${i}`}
                    className="text-sm text-slate-700 leading-snug pl-4 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:w-1.5 before:h-1.5 before:rounded-full before:bg-amber-500"
                  >
                    {g}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-400 italic">
                Nenhuma lacuna identificada.
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Footer actions */}
      <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          Arquivo: <span className="font-mono">{c.filename}</span>
        </div>
        <div className="flex items-center gap-1">
          {onReanalyze && (
            <button
              type="button"
              data-testid={`reanalyze-candidate-${c.id}`}
              onClick={handleReanalyze}
              disabled={busy}
              title="Reanalisar este candidato com a IA"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-brand-blue px-2.5 py-1.5 rounded-md hover:bg-slate-100 transition-colors disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <RefreshCw className="w-3 h-3" />
              )}
              Reanalisar
            </button>
          )}
          <button
            data-testid={`open-candidate-${c.id}`}
            onClick={() => onOpen(c)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            Enviar mensagem / gerenciar
            <ChevronDown className="w-3 h-3 -rotate-90" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CandidateCard;
